"use client";

import { Icon } from "@ospweb/sdk-ui";
import React, { useMemo } from "react";
import { cn } from "utils";

import type { FinancialConnection } from "@/src/features/api/memberApi";
// import { PlaidImportConnectionsView } from "./PlaidImportConnectionsView";
import { PlaidImportLoadingSkeleton } from "./PlaidImportLoadingSkeleton";
import { PlaidImportOnboardingView } from "./PlaidImportOnboardingView";
import { PlaidInstitutionRebindDialog } from "./PlaidInstitutionRebindDialog";
import { usePlaidConnectionsPanel } from "./usePlaidConnectionsPanel";
import type { PlaidImportModalContentProps, PlaidImportView } from "./types";

const iconButtonClass =
  "flex size-10 shrink-0 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]";

/** Deduplicate connections from invest + non-invest lists by connection_id. */
const mergeConnections = (
  invest: FinancialConnection[],
  nonInvest: FinancialConnection[],
): FinancialConnection[] => {
  const seen = new Set<string>();
  const result: FinancialConnection[] = [];
  for (const conn of [...invest, ...nonInvest]) {
    const key = conn.connection_id ?? conn.institution_id ?? "";
    if (key && !seen.has(key)) {
      seen.add(key);
      result.push(conn);
    } else if (!key) {
      result.push(conn);
    }
  }
  return result;
};

export const PlaidImportModalContent: React.FC<
  PlaidImportModalContentProps
> = ({ mode = "default", onBindSuccess, onClose }) => {
  const {
    investConnections,
    nonInvestConnections,
    isLoadingConnections,
    forceConnectionsView,
    linkLoading,
    binding,
    webLinkHost,
    rebindUpdateLinkToken,
    handleRebindCancel,
    handleRebindAdd,
    onAddOthers,
    bindingWithDelay,
    // onUpdateConnection,
  } = usePlaidConnectionsPanel({
    mode,
    onBindSuccess,
    onClose,
  });

  // Derive flat connections list for onboarding view backward compat
  const connections = useMemo(
    () => mergeConnections(investConnections, nonInvestConnections),
    [investConnections, nonInvestConnections],
  );

  const view: PlaidImportView = useMemo(() => {
    if (
      isLoadingConnections &&
      connections.length === 0 &&
      !forceConnectionsView
    ) {
      return "LOADING";
    }
    // 注释掉：总是显示 ONBOARDING 视图（连接引导界面），不显示已有连接列表
    // if (forceConnectionsView || connections.length > 0) {
    //   return "CONNECTIONS";
    // }
    return "ONBOARDING";
  }, [connections.length, forceConnectionsView, isLoadingConnections]);

  return (
    <div
      className={cn(
        "relative flex h-full min-h-0 w-full flex-col bg-background_solid_neutral_secondary pt-safe pb-safe md:pt-0 md:pb-0",
      )}
      data-testid="plaid-import-modal-content"
    >
      {webLinkHost}
      {rebindUpdateLinkToken ? (
        <PlaidInstitutionRebindDialog
          onCancel={handleRebindCancel}
          onAdd={handleRebindAdd}
        />
      ) : null}
      <div className="flex shrink-0 md:justify-end justify-start px-4 pb-3 pt-2">
        <button
          type="button"
          aria-label="Close"
          className={iconButtonClass}
          onClick={() => onClose({ action: "close" })}
        >
          <Icon name="close" size={20} className="text-content_primary" />
        </button>
      </div>

      {view === "LOADING" ? <PlaidImportLoadingSkeleton /> : null}
      {view === "ONBOARDING" ? (
        <PlaidImportOnboardingView
          loading={linkLoading}
          binding={binding || bindingWithDelay}
          onContinue={onAddOthers}
        />
      ) : null}
    </div>
  );
};
